import { useEffect, useState } from 'react';
import { ChevronLeft } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import { useAuthStore } from '../../../stores/authStore';
import { admin, palette, radii, typography } from '../../../styles/tokens';
import type { Business } from '../../../types';
import { useFieldErrors, type FieldErrors } from '../hooks/useFieldErrors';
import { AddressAutocomplete } from '../../../components/common/AddressAutocomplete';
import type { AddressResult } from '../../../components/common/AddressAutocomplete';
import { PostalCodeInput } from '../../../components/common/PostalCodeInput';
import { canonicalPostal, isValidPostal } from '../../../utils/postal';

interface BusinessProfileItemProps {
  businessId: string;
  onBack: () => void;
  onSaved: () => void;
}

const PROVINCES: { code: string; label: string }[] = [
  { code: 'ON', label: 'Ontario' },
  { code: 'QC', label: 'Quebec' },
  { code: 'BC', label: 'British Columbia' },
  { code: 'AB', label: 'Alberta' },
  { code: 'MB', label: 'Manitoba' },
  { code: 'SK', label: 'Saskatchewan' },
  { code: 'NS', label: 'Nova Scotia' },
  { code: 'NB', label: 'New Brunswick' },
  { code: 'NL', label: 'Newfoundland and Labrador' },
  { code: 'PE', label: 'Prince Edward Island' },
  { code: 'NT', label: 'Northwest Territories' },
  { code: 'NU', label: 'Nunavut' },
  { code: 'YT', label: 'Yukon' },
];

function isValidEmail(s: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s);
}

function digitsOnly(s: string): string {
  return s.replace(/\D/g, '');
}

export default function BusinessProfileItem({
  businessId,
  onBack,
  onSaved,
}: BusinessProfileItemProps) {
  const business = useAuthStore(s => s.business);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [province, setProvince] = useState('ON');
  const [postalCode, setPostalCode] = useState('');
  const [country, setCountry] = useState('');
  const [latitude, setLatitude] = useState<number | null>(null);
  const [longitude, setLongitude] = useState<number | null>(null);
  // captured per Rule #33; businesses table has no place_id column today.
  const [placeId, setPlaceId] = useState('');
  void placeId;
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');

  type FieldKey = 'name' | 'postal' | 'phone' | 'email';
  const validate = (): FieldErrors<FieldKey> => {
    const e: FieldErrors<FieldKey> = {};
    if (name.trim().length > 80) e.name = 'Must be 80 characters or fewer.';
    if (postalCode.trim() && !isValidPostal(postalCode)) {
      e.postal = 'Format should be A1A 1A1.';
    }
    if (phone.trim() && digitsOnly(phone).length < 10) {
      e.phone = 'Enter at least 10 digits.';
    }
    if (email.trim() && !isValidEmail(email.trim())) {
      e.email = 'Enter a valid email.';
    }
    return e;
  };
  const { canSave: formatOk, showError, handleBlur, markAllTouched } =
    useFieldErrors<FieldKey>(validate);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const { data, error: err } = await supabase
        .from('businesses')
        .select('name, address, city, province, postal_code, country, latitude, longitude, phone, email')
        .eq('id', businessId)
        .maybeSingle();
      if (cancelled) return;
      if (err) {
        setError(`Couldn't load business profile: ${err.message}`);
        setLoading(false);
        return;
      }
      setName(data?.name ?? '');
      setAddress(data?.address ?? '');
      setCity(data?.city ?? '');
      setProvince(data?.province || 'ON');
      setPostalCode(data?.postal_code ?? '');
      setCountry(data?.country ?? '');
      setLatitude(data?.latitude ?? null);
      setLongitude(data?.longitude ?? null);
      setPhone(data?.phone ?? '');
      setEmail(data?.email ?? '');
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [businessId]);

  const canSave = !saving && formatOk;

  async function handleSave() {
    if (!formatOk) {
      markAllTouched();
      return;
    }
    setError(null);
    setSaving(true);
    try {
      const phoneDigits = digitsOnly(phone);
      const payload = {
        name: name.trim() || null,
        address: address.trim() || null,
        city: city.trim() || null,
        province: province || null,
        postal_code: postalCode.trim() ? canonicalPostal(postalCode) : null,
        country: country || null,
        latitude: latitude,
        longitude: longitude,
        phone: phoneDigits || null,
        email: email.trim() || null,
      };
      const { error: e1 } = await supabase
        .from('businesses')
        .update(payload)
        .eq('id', businessId);
      if (e1) throw e1;

      // Patch authStore so the welcome card and other surfaces see the change
      // without needing a full re-init.
      if (business) {
        useAuthStore.setState({ business: { ...business, ...payload } as Business });
      }

      setSaving(false);
      onSaved();
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e);
      setError(`Couldn't save business profile: ${message}`);
      setSaving(false);
    }
  }

  return (
    <div style={{ maxWidth: 720, margin: '0 auto', padding: '8px 4px 0' }}>
      <button
        type="button"
        onClick={onBack}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
          padding: '6px 8px 6px 4px',
          marginBottom: 16,
          background: 'transparent',
          border: 'none',
          color: admin.caption,
          fontSize: typography.size.caption,
          cursor: 'pointer',
          fontFamily: 'inherit',
        }}
      >
        <ChevronLeft size={16} />
        <span>Setup guide</span>
        <span style={{ color: palette.g400, margin: '0 4px' }}>›</span>
        <span style={{ color: admin.body }}>Tell us about your business</span>
      </button>

      <h1
        style={{
          margin: 0,
          fontSize: typography.size.title,
          fontWeight: typography.weight.bold,
          color: admin.header,
          letterSpacing: '-0.01em',
        }}
      >
        Tell us about your business
      </h1>
      <p
        style={{
          margin: '8px 0 24px',
          fontSize: typography.size.body,
          color: admin.body,
          lineHeight: typography.lineHeight.relaxed,
          maxWidth: 560,
        }}
      >
        The basics — we'll use this on your invoices, your booking page, and everywhere clients
        see your business.
      </p>

      {loading ? (
        <div style={{ fontSize: typography.size.caption, color: admin.caption }}>Loading…</div>
      ) : (
        <>
          <div
            style={{
              background: palette.white,
              border: `1px solid ${palette.g150}`,
              borderRadius: radii.md,
              padding: 20,
              marginBottom: 24,
              display: 'flex',
              flexDirection: 'column',
              gap: 16,
            }}
          >
            <Field
              label="Business name"
              hint="This is what clients see on your invoices and booking page."
              error={showError('name')}
            >
              <input
                type="text"
                value={name}
                onChange={e => setName(e.target.value)}
                onBlur={() => handleBlur('name')}
                maxLength={80}
                placeholder="e.g. Grin's Barbershop"
                style={inputStyleFor(showError('name'))}
              />
            </Field>

            {/* Google Places autocomplete — selecting a place auto-fills
                city / province / postal code. Server-side geocoding still
                runs on save (resolve_user_region populates metro_id etc.). */}
            <Field
              label="Street address"
              hint="For home-based businesses, your home address is fine."
            >
              <AddressAutocomplete
                initialValue={address}
                onSelect={(r: AddressResult) => {
                  setAddress(r.address);
                  if (r.city) setCity(r.city);
                  if (r.province) setProvince(r.province);
                  if (r.postalCode) setPostalCode(r.postalCode);
                  if (r.country) setCountry(r.country);
                  setLatitude(r.latitude);
                  setLongitude(r.longitude);
                  if (r.placeId) setPlaceId(r.placeId);
                }}
                placeholder="e.g. 123 Main Street"
              />
            </Field>

            <div style={{ display: 'flex', gap: 12 }}>
              <div style={{ flex: 2 }}>
                <Field label="City">
                  <input
                    type="text"
                    value={city}
                    onChange={e => setCity(e.target.value)}
                    placeholder="Toronto"
                    style={inputStyle}
                  />
                </Field>
              </div>
              <div style={{ flex: 1 }}>
                <Field label="Province">
                  <select
                    value={province}
                    onChange={e => setProvince(e.target.value)}
                    style={inputStyle}
                  >
                    {PROVINCES.map(p => (
                      <option key={p.code} value={p.code}>
                        {p.label}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>
              <div style={{ flex: 1 }}>
                <Field label="Postal code">
                  <PostalCodeInput
                    value={postalCode}
                    onChange={setPostalCode}
                    onBlur={() => handleBlur('postal')}
                    ariaLabel="Postal code"
                    error={showError('postal') ?? undefined}
                  />
                </Field>
              </div>
            </div>

            <Field
              label="Business phone"
              hint="The number clients call. A cell phone is fine."
              error={showError('phone')}
            >
              <input
                type="tel"
                value={phone}
                onChange={e => setPhone(e.target.value)}
                onBlur={() => handleBlur('phone')}
                placeholder="(416) 555-0123"
                style={inputStyleFor(showError('phone'))}
              />
            </Field>

            <Field
              label="Business email"
              hint="Where invoice replies and client messages get sent."
              error={showError('email')}
            >
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                onBlur={() => handleBlur('email')}
                placeholder="hello@yourbusiness.ca"
                style={inputStyleFor(showError('email'))}
              />
            </Field>
          </div>

          {error && (
            <div
              style={{
                background: palette.redWash,
                border: `1px solid ${palette.red}`,
                borderRadius: radii.md,
                padding: 12,
                marginBottom: 16,
                color: palette.red,
                fontSize: typography.size.caption,
              }}
            >
              {error}
            </div>
          )}

          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            <button
              type="button"
              onClick={handleSave}
              disabled={!canSave}
              style={{
                padding: '10px 20px',
                borderRadius: radii.md,
                border: 'none',
                background: canSave ? palette.accent : palette.g200,
                color: palette.white,
                fontSize: typography.size.body,
                fontWeight: typography.weight.semibold,
                cursor: canSave ? 'pointer' : 'not-allowed',
                fontFamily: 'inherit',
              }}
            >
              {saving ? 'Saving…' : 'Save business profile'}
            </button>
            <button
              type="button"
              onClick={onBack}
              disabled={saving}
              style={{
                padding: '10px 16px',
                borderRadius: radii.md,
                border: 'none',
                background: 'transparent',
                color: admin.caption,
                fontSize: typography.size.body,
                cursor: saving ? 'not-allowed' : 'pointer',
                fontFamily: 'inherit',
              }}
            >
              Cancel
            </button>
          </div>
        </>
      )}
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  padding: '8px 10px',
  borderRadius: radii.sm,
  border: `1px solid ${palette.g200}`,
  fontSize: typography.size.body,
  fontFamily: 'inherit',
  outline: 'none',
  background: palette.white,
  width: '100%',
  boxSizing: 'border-box',
};

function inputStyleFor(error: string | null): React.CSSProperties {
  if (!error) return inputStyle;
  return { ...inputStyle, border: `1px solid ${palette.red}` };
}

function Field({
  label,
  hint,
  error,
  children,
}: {
  label: string;
  hint?: string;
  error?: string | null;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div
        style={{
          fontSize: typography.size.caption,
          fontWeight: typography.weight.semibold,
          color: admin.header,
          marginBottom: 6,
        }}
      >
        {label}
      </div>
      {children}
      {hint && !error && (
        <div
          style={{
            fontSize: typography.size.micro,
            color: admin.caption,
            marginTop: 4,
          }}
        >
          {hint}
        </div>
      )}
      {error && (
        <div
          style={{
            fontSize: typography.size.micro,
            color: palette.red,
            marginTop: 4,
            fontWeight: typography.weight.medium,
          }}
        >
          {error}
        </div>
      )}
    </div>
  );
}
